"use client";

import {
  type Icon as PhosphorIcon,
  type IconProps as PhosphorIconProps,
} from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { SR_ICONS } from "../lib/srIcons";
import { cn } from "../lib/utils";

const ICON_PRESET_CLASSES = {
  size: {
    "!xs": "!size-3.5",
    xs: "size-3.5",
    "!sm": "!size-4",
    sm: "size-4",
    "!default": "!size-[1.125rem]",
    default: "size-[1.125rem]",
    "!lg": "!size-5",
    lg: "size-5",
    "!xl": "!size-6",
    xl: "size-6",
    "!2xl": "!size-7",
    "2xl": "size-7",
    "!3xl": "!size-8",
    "3xl": "size-8",
  },
  sizeForSVG: {
    "!xs": "![&>svg]:size-3.5",
    xs: "[&>svg]:size-3.5",
    "!sm": "![&>svg]:size-4",
    sm: "[&>svg]:size-4",
    "!default": "![&>svg]:size-[1.125rem]",
    default: "[&>svg]:size-[1.125rem]",
    "!lg": "![&>svg]:size-5",
    lg: "[&>svg]:size-5",
    "!xl": "![&>svg]:size-6",
    xl: "[&>svg]:size-6",
    "!2xl": "![&>svg]:size-7",
    "2xl": "[&>svg]:size-7",
    "!3xl": "![&>svg]:size-8",
    "3xl": "[&>svg]:size-8",
  },
} as const;

const iconVariants = cva(null, {
  variants: {
    size: ICON_PRESET_CLASSES.size,
  },
  defaultVariants: {
    size: "default",
  },
});

type IconKey = keyof typeof SR_ICONS;
interface IconProps
  extends Omit<PhosphorIconProps, "size">, VariantProps<typeof iconVariants> {
  icon: PhosphorIcon | IconKey;
}

type IconSVG = IconProps["icon"];

const IconKeys = Object.keys(SR_ICONS) as IconKey[];

type IconComponent = React.ReactElement<PhosphorIcon | IconProps>;

const Icon = ({
  className,
  icon: Icon,
  size,
  ...props
}: IconProps): IconComponent => {
  const Component = typeof Icon === "string" ? SR_ICONS[Icon] : Icon;

  return (
    <Component {...props} className={cn(iconVariants({ size, className }))} />
  );
};
Icon.displayName = "Icon";

export {
  Icon,
  ICON_PRESET_CLASSES,
  IconKeys,
  iconVariants,
  type IconComponent,
  type IconKey,
  type IconProps,
  type IconSVG,
};
